//
//  breadcrumbs.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module derives structural breadcrumbs from application routes.
//

import AppSettings from "src/AppSettings";
import type { AppIconDefinition } from "src/components/common/Icon";
import type { ILayoutBreadcrumb } from "src/contexts/layouts/LayoutContext";

import { collectionBreadcrumbStyle } from "./breadcrumbIconStyle";

const breadcrumb = (
    code: string,
    label: string,
    href: string,
    icon: AppIconDefinition,
    order: number,
    iconStyle?: ILayoutBreadcrumb["iconStyle"]
): ILayoutBreadcrumb => ({ code, label, href, icon, order, iconStyle });

const trialSections: Record<string, [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]> =
    {
        details: ["Details", AppSettings.icons.view],
        milestones: ["Milestones", AppSettings.icons.models.trialMilestones],
        institutions: [
            "Institutions",
            AppSettings.icons.models.trialInstitution,
            collectionBreadcrumbStyle("trialInstitution")
        ],
        personnel: [
            "Personnel",
            AppSettings.icons.models.personnelRole,
            collectionBreadcrumbStyle("personnelRole")
        ],
        participants: [
            "Participants",
            AppSettings.icons.models.participant,
            collectionBreadcrumbStyle("participant")
        ],
        items: ["Items", AppSettings.icons.models.item, collectionBreadcrumbStyle("item")],
        forms: [
            "Forms",
            AppSettings.icons.models.trialForm,
            collectionBreadcrumbStyle("trialForm")
        ],
        groups: [
            "Groups",
            AppSettings.icons.models.trialGroup,
            collectionBreadcrumbStyle("trialGroup")
        ],
        treatments: [
            "Treatments",
            AppSettings.icons.models.trialTreatment,
            collectionBreadcrumbStyle("trialTreatment")
        ],
        notes: [
            "Notes",
            AppSettings.icons.models.trialNote,
            collectionBreadcrumbStyle("trialNote")
        ],
        trackers: [
            "QA Tracker",
            AppSettings.icons.models.trialTracker,
            collectionBreadcrumbStyle("trialTracker")
        ],
        configuration: ["Configuration", AppSettings.icons.settings]
    };

const trialInstitutionSections: Record<
    string,
    [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
> = {
    details: ["Details", AppSettings.icons.view],
    "supporting-sites": [
        "Supporting Sites",
        AppSettings.icons.models.trialInstitutionSupportingSite,
        collectionBreadcrumbStyle("trialInstitutionSupportingSite")
    ],
    capabilities: [
        "Capabilities",
        AppSettings.icons.models.trialInstitutionCapability,
        collectionBreadcrumbStyle("trialInstitutionCapability")
    ],
    participants: [
        "Participants",
        AppSettings.icons.models.participant,
        collectionBreadcrumbStyle("participant")
    ],
    items: ["Items", AppSettings.icons.models.item, collectionBreadcrumbStyle("item")],
    personnel: [
        "Personnel",
        AppSettings.icons.models.personnelRole,
        collectionBreadcrumbStyle("personnelRole")
    ],
    notes: [
        "Notes",
        AppSettings.icons.models.trialInstitutionNote,
        collectionBreadcrumbStyle("trialInstitutionNote")
    ]
};

const institutionSections: Record<
    string,
    [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
> = {
    details: ["Details", AppSettings.icons.view],
    departments: [
        "Departments",
        AppSettings.icons.models.department,
        collectionBreadcrumbStyle("department")
    ],
    personnel: [
        "Personnel",
        AppSettings.icons.models.personnelRole,
        collectionBreadcrumbStyle("personnelRole")
    ],
    forms: [
        "Forms",
        AppSettings.icons.models.institutionForm,
        collectionBreadcrumbStyle("institutionForm")
    ],
    notes: [
        "Notes",
        AppSettings.icons.models.institutionNote,
        collectionBreadcrumbStyle("institutionNote")
    ]
};

const groupSections: Record<string, [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]> =
    {
        details: ["Details", AppSettings.icons.view],
        personnel: [
            "Personnel",
            AppSettings.icons.models.personnelRole,
            collectionBreadcrumbStyle("personnelRole")
        ],
        trials: ["Trials", AppSettings.icons.models.trial, collectionBreadcrumbStyle("trial")],
        items: ["Items", AppSettings.icons.models.item, collectionBreadcrumbStyle("item")],
        notes: [
            "Notes",
            AppSettings.icons.models.groupNote,
            collectionBreadcrumbStyle("groupNote")
        ],
        configuration: ["Configuration", AppSettings.icons.settings]
    };

const personnelSections: Record<
    string,
    [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
> = {
    details: ["Details", AppSettings.icons.view],
    files: [
        "Files",
        AppSettings.icons.models.sourceDocument,
        collectionBreadcrumbStyle("sourceDocument")
    ],
    hubspot: [
        "HubSpot",
        AppSettings.icons.models.personnel,
        collectionBreadcrumbStyle("personnel")
    ],
    roles: [
        "Institutions & Roles",
        AppSettings.icons.models.personnelRole,
        collectionBreadcrumbStyle("personnelRole")
    ],
    permissions: [
        "Permissions",
        AppSettings.icons.models.permission,
        collectionBreadcrumbStyle("permission")
    ],
    notes: [
        "Notes",
        AppSettings.icons.models.personnelNote,
        collectionBreadcrumbStyle("personnelNote")
    ],
    activedirectory: ["Login & Security", AppSettings.icons.locked],
    openclinica: ["OpenClinica", AppSettings.icons.externalLink]
};

const settingsSections: Record<
    string,
    [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
> = {
    general: ["General", AppSettings.icons.settings],
    trial: ["Trials", AppSettings.icons.models.trial, collectionBreadcrumbStyle("trial")],
    item: ["Items", AppSettings.icons.models.item, collectionBreadcrumbStyle("item")],
    institution: [
        "Institutions",
        AppSettings.icons.models.institution,
        collectionBreadcrumbStyle("institution")
    ],
    participant: [
        "Participants",
        AppSettings.icons.models.participant,
        collectionBreadcrumbStyle("participant")
    ],
    qa: ["QA", AppSettings.icons.required],
    hubspot: [
        "HubSpot",
        AppSettings.icons.models.personnel,
        collectionBreadcrumbStyle("personnel")
    ],
    openclinicauserroles: [
        "OpenClinica User Roles",
        AppSettings.icons.models.openClinicaUserRole,
        collectionBreadcrumbStyle("openClinicaUserRole")
    ]
};

const settingsLeaves: Record<string, [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]> =
    {
        roles: ["Roles", AppSettings.icons.models.role, collectionBreadcrumbStyle("role")],
        notices: ["Notices", AppSettings.icons.models.notice, collectionBreadcrumbStyle("notice")],
        help: [
            "Help Coverage",
            AppSettings.icons.models.helpTopic,
            collectionBreadcrumbStyle("helpTopic")
        ],
        countries: [
            "Countries",
            AppSettings.icons.models.country,
            collectionBreadcrumbStyle("country")
        ],
        states: ["States", AppSettings.icons.models.state, collectionBreadcrumbStyle("state")],
        itemcomponenttypes: [
            "Item Component Types",
            AppSettings.icons.models.itemComponentType,
            collectionBreadcrumbStyle("itemComponentType")
        ],
        notetypes: [
            "Note Types",
            AppSettings.icons.models.noteType,
            collectionBreadcrumbStyle("noteType")
        ],
        genders: ["Genders", AppSettings.icons.models.gender, collectionBreadcrumbStyle("gender")],
        interests: [
            "Interests",
            AppSettings.icons.models.interest,
            collectionBreadcrumbStyle("interest")
        ],
        membershiptypes: [
            "Membership Types",
            AppSettings.icons.models.membershipType,
            collectionBreadcrumbStyle("membershipType")
        ],
        professionaldisciplines: [
            "Professional Disciplines",
            AppSettings.icons.models.professionalDiscipline,
            collectionBreadcrumbStyle("professionalDiscipline")
        ],
        salutations: [
            "Salutations",
            AppSettings.icons.models.salutation,
            collectionBreadcrumbStyle("salutation")
        ],
        trialcategorys: [
            "Trial Categories",
            AppSettings.icons.models.trialCategory,
            collectionBreadcrumbStyle("trialCategory")
        ],
        trialtypes: [
            "Trial Types",
            AppSettings.icons.models.trialType,
            collectionBreadcrumbStyle("trialType")
        ],
        tumourgroups: [
            "Tumour Groups",
            AppSettings.icons.models.tumourGroup,
            collectionBreadcrumbStyle("tumourGroup")
        ],
        allocatedwps: [
            "Allocated WPs",
            AppSettings.icons.models.allocatedWP,
            collectionBreadcrumbStyle("allocatedWP")
        ],
        trialphases: [
            "Trial Phases",
            AppSettings.icons.models.trialPhase,
            collectionBreadcrumbStyle("trialPhase")
        ],
        involvements: [
            "Involvement",
            AppSettings.icons.models.involvement,
            collectionBreadcrumbStyle("involvement")
        ],
        trialanalysistypes: [
            "Analysis Types",
            AppSettings.icons.models.trialAnalysisType,
            collectionBreadcrumbStyle("trialAnalysisType")
        ],
        trialpresentationtypes: [
            "Presentation Types",
            AppSettings.icons.models.trialPresentationType,
            collectionBreadcrumbStyle("trialPresentationType")
        ],
        trialpublicationtypes: [
            "Publication Types",
            AppSettings.icons.models.trialPublicationType,
            collectionBreadcrumbStyle("trialPublicationType")
        ],
        sitelocations: [
            "Site Locations",
            AppSettings.icons.models.siteLocation,
            collectionBreadcrumbStyle("siteLocation")
        ],
        facilityalliancemembers: [
            "Facility Alliance Members",
            AppSettings.icons.models.facilityAllianceMember,
            collectionBreadcrumbStyle("facilityAllianceMember")
        ],
        institutiontypes: [
            "Institution Types",
            AppSettings.icons.models.institutionType,
            collectionBreadcrumbStyle("institutionType")
        ],
        "capability-categories": [
            "Capability Categories",
            AppSettings.icons.models.institutionCapabilityCategory,
            collectionBreadcrumbStyle("institutionCapabilityCategory")
        ],
        capabilities: [
            "Capabilities",
            AppSettings.icons.models.institutionCapability,
            collectionBreadcrumbStyle("institutionCapability")
        ],
        "tag-categories": [
            "Tag Categories",
            AppSettings.icons.models.institutionTagCategory,
            collectionBreadcrumbStyle("institutionTagCategory")
        ],
        tags: [
            "Tags",
            AppSettings.icons.models.institutionTag,
            collectionBreadcrumbStyle("institutionTag")
        ],
        formtemplates: [
            "Form Templates",
            AppSettings.icons.models.formTemplate,
            collectionBreadcrumbStyle("formTemplate")
        ],
        formcomponents: [
            "Form Components",
            AppSettings.icons.models.itemComponent,
            collectionBreadcrumbStyle("itemComponent")
        ],
        reportingtags: [
            "Reporting Tags",
            AppSettings.icons.models.reportingTag,
            collectionBreadcrumbStyle("reportingTag")
        ],
        reportingtagcategories: [
            "Reporting Tag Categories",
            AppSettings.icons.models.reportingTagCategory,
            collectionBreadcrumbStyle("reportingTagCategory")
        ],
        tasktypes: [
            "Task Types",
            AppSettings.icons.models.taskType,
            collectionBreadcrumbStyle("taskType")
        ],
        trackertypes: [
            "Tracker Types",
            AppSettings.icons.models.trackerType,
            collectionBreadcrumbStyle("trackerType")
        ],
        trackermilestones: [
            "Tracker Milestones",
            AppSettings.icons.models.trackerMilestone,
            collectionBreadcrumbStyle("trackerMilestone")
        ],
        formtemplatefiletypes: [
            "File Types",
            AppSettings.icons.models.formTemplateFileType,
            collectionBreadcrumbStyle("formTemplateFileType")
        ],
        qaemailtemplates: [
            "QA Email Templates",
            AppSettings.icons.models.standardEmailTemplate,
            collectionBreadcrumbStyle("standardEmailTemplate")
        ],
        itemcomponents: [
            "Item Components",
            AppSettings.icons.models.itemComponent,
            collectionBreadcrumbStyle("itemComponent")
        ],
        itemcategories: [
            "Item Categories",
            AppSettings.icons.models.category,
            collectionBreadcrumbStyle("category")
        ],
        itemtemplates: [
            "Trial Institution Item Templates",
            AppSettings.icons.models.trialInstitutionItemTemplate,
            collectionBreadcrumbStyle("trialInstitutionItemTemplate")
        ],
        "trial-item-templates": [
            "Trial Item Templates",
            AppSettings.icons.models.trialItemTemplate,
            collectionBreadcrumbStyle("trialItemTemplate")
        ],
        "group-item-templates": [
            "Trial Group Item Templates",
            AppSettings.icons.models.item,
            collectionBreadcrumbStyle("item")
        ]
    };

const participantSettingsLeaves: Record<
    string,
    [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
> = {
    "tag-categories": [
        "Participant Tag Categories",
        AppSettings.icons.models.patientTagCategory,
        collectionBreadcrumbStyle("patientTagCategory")
    ],
    tags: [
        "Participant Tags",
        AppSettings.icons.models.patientTag,
        collectionBreadcrumbStyle("patientTag")
    ]
};

/**
 * Appends a mapped structural breadcrumb to the supplied list.
 *
 * Postconditions: Missing or unmapped segments leave the list unchanged. Lookup lowercases the
 * segment while the supplied href remains unchanged; the order also determines the generated code.
 */
const addSection = (
    crumbs: ILayoutBreadcrumb[],
    mapping: Record<string, [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]>,
    segment: string | undefined,
    href: string,
    order: number
) => {
    const definition = segment ? mapping[segment.toLowerCase()] : undefined;
    if (definition) {
        crumbs.push(
            breadcrumb(`section-${order}`, definition[0], href, definition[1], order, definition[2])
        );
    }
};

/**
 * Builds structural breadcrumbs from recognized pathname segments.
 *
 * Preconditions: Supply a pathname; this helper does not strip query strings or fragments.
 * Postconditions: Returns a fresh list with known collection/section metadata and no loaded identity
 * labels. Empty or unknown roots return an empty list; unknown nested sections add nothing.
 * Root and mapping lookup are case-insensitive, but create guards and nested branch checks retain
 * their case-sensitive comparisons. IDs are interpolated without validation or decoding.
 */
export const createStaticBreadcrumbs = (pathname: string): ILayoutBreadcrumb[] => {
    const segments = pathname.split("/").filter(Boolean);
    const root = segments[0]?.toLowerCase();
    const crumbs: ILayoutBreadcrumb[] = [];

    if (!root) {
        return crumbs;
    }

    if (root === "dashboard") {
        return [
            breadcrumb("dashboard", "Dashboard", "/dashboard", AppSettings.icons.dashboard, 10)
        ];
    }
    if (root === "emails") {
        return [breadcrumb("email", "Email Preview", pathname, AppSettings.icons.preview, 10)];
    }
    if (root === "help") {
        return [breadcrumb("help", "Help", "/help", AppSettings.icons.help, 10)];
    }

    if (root === "trials") {
        crumbs.push(
            breadcrumb(
                "trials",
                "Trials",
                "/trials",
                AppSettings.icons.models.trial,
                10,
                collectionBreadcrumbStyle("trial")
            )
        );
        if (segments[1] === "create") {
            return crumbs;
        }

        const trialId = segments[1];
        const section = segments[2];
        if (trialId && section) {
            addSection(crumbs, trialSections, section, `/trials/${trialId}/${section}`, 30);
        }

        if (section === "institutions" && segments[3] && segments[4]) {
            const child = segments[4];
            addSection(
                crumbs,
                trialInstitutionSections,
                child,
                `/trials/${trialId}/institutions/${segments[3]}/${child}`,
                50
            );

            // A supporting site adds a level of its own, so its Details/Personnel section sits
            // after the supporting site identity crumb (60) rather than alongside its siblings.
            if (
                child === "supporting-sites" &&
                segments[5] &&
                segments[5] !== "create" &&
                segments[6]
            ) {
                addSection(
                    crumbs,
                    {
                        details: ["Details", AppSettings.icons.view],
                        personnel: [
                            "Personnel",
                            AppSettings.icons.models.personnelRole,
                            collectionBreadcrumbStyle("personnelRole")
                        ]
                    },
                    segments[6],
                    `/trials/${trialId}/institutions/${segments[3]}` +
                        `/supporting-sites/${segments[5]}/${segments[6]}`,
                    70
                );
            }
        } else if (section === "groups" && segments[3] && segments[4]) {
            addSection(
                crumbs,
                {
                    details: ["Details", AppSettings.icons.view],
                    items: [
                        "Items",
                        AppSettings.icons.models.trialGroupItem,
                        collectionBreadcrumbStyle("trialGroupItem")
                    ]
                },
                segments[4],
                `/trials/${trialId}/groups/${segments[3]}/${segments[4]}`,
                50
            );
        } else if (section === "configuration" && segments[3]) {
            const configurationSections: Record<
                string,
                [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
            > = {
                capabilities: [
                    "Institution Capabilities",
                    AppSettings.icons.models.trialCapability,
                    collectionBreadcrumbStyle("trialCapability")
                ],
                tags: [
                    "Institution Tags",
                    AppSettings.icons.models.trialTag,
                    collectionBreadcrumbStyle("trialTag")
                ],
                "institution-item-templates": [
                    "Institution Item Templates",
                    AppSettings.icons.models.trialInstitutionItemTemplate,
                    collectionBreadcrumbStyle("trialInstitutionItemTemplate")
                ],
                "group-item-templates": [
                    "Trial Group Item Templates",
                    AppSettings.icons.models.item,
                    collectionBreadcrumbStyle("item")
                ],
                api: ["API", AppSettings.icons.models.trialApi]
            };
            addSection(
                crumbs,
                configurationSections,
                segments[3],
                `/trials/${trialId}/configuration/${segments[3]}`,
                40
            );
        } else if (section === "items" && segments[3]) {
            const itemSections: Record<
                string,
                [string, AppIconDefinition, ILayoutBreadcrumb["iconStyle"]?]
            > = {
                "all-items": ["All Items", AppSettings.icons.models.item],
                "trial-items": [
                    "Trial Items",
                    AppSettings.icons.models.trialItem,
                    collectionBreadcrumbStyle("trialItem")
                ],
                "institution-items": [
                    "Institution Items",
                    AppSettings.icons.models.trialInstitutionItem,
                    collectionBreadcrumbStyle("trialInstitutionItem")
                ],
                "group-items": [
                    "Group Items",
                    AppSettings.icons.models.trialGroupItem,
                    collectionBreadcrumbStyle("trialGroupItem")
                ]
            };
            addSection(
                crumbs,
                itemSections,
                segments[3],
                `/trials/${trialId}/items/${segments[3]}`,
                40
            );
        }

        return crumbs;
    }

    if (root === "institutions") {
        crumbs.push(
            breadcrumb(
                "institutions",
                "Institutions",
                "/institutions",
                AppSettings.icons.models.institution,
                10,
                collectionBreadcrumbStyle("institution")
            )
        );
        const institutionId = segments[1];
        if (institutionId && institutionId !== "create" && segments[2]) {
            addSection(
                crumbs,
                institutionSections,
                segments[2],
                `/institutions/${institutionId}/${segments[2]}`,
                30
            );
            if (segments[2] === "departments" && segments[3] && segments[4]) {
                addSection(
                    crumbs,
                    {
                        details: ["Details", AppSettings.icons.view],
                        personnel: [
                            "Personnel",
                            AppSettings.icons.models.personnel,
                            collectionBreadcrumbStyle("personnel")
                        ]
                    },
                    segments[4],
                    `/institutions/${institutionId}/departments/${segments[3]}/${segments[4]}`,
                    50
                );
            }
        }
        return crumbs;
    }

    if (root === "groups") {
        crumbs.push(
            breadcrumb(
                "groups",
                "Groups",
                "/groups",
                AppSettings.icons.models.group,
                10,
                collectionBreadcrumbStyle("group")
            )
        );
        if (segments[1] && segments[1] !== "create" && segments[2]) {
            addSection(
                crumbs,
                groupSections,
                segments[2],
                `/groups/${segments[1]}/${segments[2]}`,
                30
            );
            if (segments[2] === "configuration" && segments[3] === "items") {
                addSection(
                    crumbs,
                    {
                        items: [
                            "Trial Group Item Templates",
                            AppSettings.icons.models.item,
                            collectionBreadcrumbStyle("item")
                        ]
                    },
                    segments[3],
                    `/groups/${segments[1]}/configuration/items`,
                    40
                );
            }
        }
        return crumbs;
    }

    if (root === "personnel") {
        crumbs.push(
            breadcrumb(
                "personnel",
                "Personnel",
                "/personnel",
                AppSettings.icons.models.personnel,
                10,
                collectionBreadcrumbStyle("personnel")
            )
        );
        if (segments[1] && segments[1] !== "create" && segments[2]) {
            addSection(
                crumbs,
                personnelSections,
                segments[2],
                `/personnel/${segments[1]}/${segments[2]}`,
                30
            );
        }
        return crumbs;
    }

    if (root === "settings") {
        crumbs.push(
            breadcrumb("settings", "Settings", "/settings", AppSettings.icons.settings, 10)
        );
        if (segments[1]) {
            addSection(crumbs, settingsSections, segments[1], `/settings/${segments[1]}`, 20);
        }
        if (segments[2]) {
            addSection(
                crumbs,
                segments[1]?.toLowerCase() === "participant"
                    ? participantSettingsLeaves
                    : settingsLeaves,
                segments[2],
                `/settings/${segments[1]}/${segments[2]}`,
                30
            );
        }
        return crumbs;
    }

    return crumbs;
};
